import { map, assign, isObject } from 'lodash';
import { apiAxios } from '@/modules/core/app/helpers/AxiosHelper';
import { DownloadUtil } from '@/modules/core/app/utils/DownloadUtil';
import { LIST } from '@/modules/core/app/constants/api.constants';
import { deduplicatedRequest } from '@/modules/core/app/utils/RequestDeduplicator';

export class BaseResource {
  /**
   * Represents the base route of an API module endpoint
   *
   * @type {RouteGroup}
   */
  routeGroup;

  /**
   * Represents axiosInstance for the API
   *
   * @type {AxiosInstance}
   */
  apiHelper;

  /**
   * @param routeGroup
   * @param apiHelper
   */
  constructor(routeGroup, apiHelper = apiAxios) {
    this.routeGroup = routeGroup;
    this.apiHelper = apiHelper;
  }

  /**
   * @param model
   * @param route
   * @param params
   * @param config
   * @returns {*}
   */
  async getData(model, route, params = {}, config = {}) {
    const url = this._buildRoute(route);
    const key = `${url}|${JSON.stringify(params)}`;
    const data = await deduplicatedRequest(key, () =>
      this.apiHelper.get(url, { params, ...config })
    );
    return this.extractData(data, model, params);
  }

  /**
   * @param model
   * @param route
   * @param params
   * @param config
   * @returns {*}
   */
  async getDataUsingPost(model, route, params = {}, config = {}) {
    const data = await this.apiHelper.post(this._buildRoute(route), params, config);
    return this.extractData(data, model, params);
  }

  /**
   * @param data
   * @param model
   * @param params
   * @returns {*}
   */
  extractData(data, model, params) {
    if (data === null) {
      return data;
    }
    // Return a fresh object instead of mutating `data`. Concurrent identical requests share the
    // same deduplicated response (see RequestDeduplicator), so mutating it here would corrupt the
    // other caller's mapping (e.g. re-mapping already-built models).
    if (params.datatable && data?.aaData) {
      return { ...data, aaData: this._mapModel(model, data.aaData) };
    }
    if (params.datatable && data?.data?.aaData) {
      const extracted = { ...data, aaData: this._mapModel(model, data.data.aaData) };
      if (!extracted.iTotalDisplayRecords && data.data.iTotalDisplayRecords) {
        extracted.iTotalDisplayRecords = data.data.iTotalDisplayRecords;
      }
      return extracted;
    }
    if (Array.isArray(data?.values)) {
      return { ...data, values: map(data.values, (value) => this._mapModel(model, value)) };
    }
    if (isObject(data)) {
      return this._mapModel(model, data);
    }
    return data;
  }

  /**
   * @param data
   * @param route
   * @param config
   * @returns {*}
   */
  async postData(data, route, config = {}) {
    return this.apiHelper.post(this._buildRoute(route), data, config);
  }

  /**
   * @param data
   * @param route
   * @param config
   * @returns {*}
   */
  async putData(data, route, config = {}) {
    return this.apiHelper.put(this._buildRoute(route), data, config);
  }

  /**
   * @param route
   * @param config
   * @returns {*}
   */
  async deleteData(route, config = {}) {
    return this.apiHelper.delete(this._buildRoute(route), config);
  }

  /**
   * @param route
   * @param params
   * @param config
   * @returns {*}
   */
  async getColumns(route, params = {}, config = {}) {
    params = assign({ metadata: true }, params);
    return this.apiHelper.get(this._buildRoute(route), { params, ...config });
  }

  /**
   * @protected
   *
   * Show downloaded file in browser
   * @param response
   * @param {string} fallbackFileName - used when the response carries no
   *   content-disposition (e.g. stripped by a proxy); defaults to the generic LIST name.
   */
  showDownload(response, fallbackFileName = LIST) {
    const responseUrl = window.URL.createObjectURL(
      new Blob([response.data], {
        type: response.data?.type,
      })
    );
    const contentDisposition = response.headers['content-disposition'];
    let fileName = fallbackFileName;
    if (contentDisposition) {
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (utf8Match && utf8Match[1]) {
        fileName = decodeURIComponent(utf8Match[1]);
      } else {
        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/);
        if (asciiMatch && asciiMatch[1]) {
          try {
            // HTTP headers are read as Latin-1 by default; if the server sent raw UTF-8
            // bytes in a plain filename="..." field, reinterpret those bytes as UTF-8.
            const bytes = new Uint8Array(asciiMatch[1].split('').map((c) => c.charCodeAt(0)));
            fileName = new TextDecoder('utf-8').decode(bytes);
          } catch (e) {
            [, fileName] = asciiMatch;
          }
        }
      }
    }
    try {
      DownloadUtil.download(responseUrl, fileName);
    } finally {
      window.URL.revokeObjectURL(responseUrl);
    }
  }

  /**
   * Download Json content as a file.
   * @param response
   * @param fileName
   */
  downloadJson(response, fileName) {
    const output = `data:text/' + type + ';charset=utf-8, ${JSON.stringify(response, null, 2)}`;
    let encodedUri = encodeURI(output);
    encodedUri = encodedUri.replace('#', '%23');
    fileName += `.json`;
    DownloadUtil.download(encodedUri, fileName);
  }

  /**
   * @param route
   * @returns {*}
   * @protected
   */
  _buildRoute(route) {
    return route ? `${this.routeGroup}/${route}` : this.routeGroup;
  }

  /**
   * @param model
   * @param data
   * @returns {Array}
   * @private
   */
  _mapModel(model, data) {
    if (data instanceof Array) {
      return map(data, (datum) => model.factory(datum));
    }
    return model.factory(data);
  }
}
